import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../App';
import Navigation from './Navigation';
import Footer from './Footer';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Wallet, 
  Plus, 
  Clock, 
  AlertCircle,
  Trash2,
  Upload,
  File,
  FileText,
  Image,
  X,
  Download,
  Building,
  Scale,
  Receipt,
  Wrench,
  Users,
  Calculator,
  Home
} from 'lucide-react';

const ExpenseEntryFixed = () => {
  const { user, API } = useContext(UserContext);
  
  // Form states
  const [selectedType, setSelectedType] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [justifications, setJustifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [recentEntries, setRecentEntries] = useState([]);

  // Fixed expense types (no API call needed)
  const expenseTypes = [
    {id: "mairie", name: "Dépenses de mairie"},
    {id: "ministere_commerce", name: "Négociations Ministère du commerce"},
    {id: "impots", name: "Paiement des impôts"},
    {id: "reparations", name: "Réparations de matériels de boulangerie"},
    {id: "salaires", name: "Salaires des travailleurs"},
    {id: "honoraires", name: "Honoraires comptable"},
    {id: "factures", name: "Factures"},
    {id: "loyer", name: "Loyer"}
  ];

  const expenseIcons = {
    'mairie': Building,
    'ministere_commerce': Scale,
    'impots': Receipt,
    'reparations': Wrench,
    'salaires': Users,
    'honoraires': Calculator,
    'factures': FileText,
    'loyer': Home
  };

  const expenseColors = {
    'mairie': 'bg-blue-100 text-blue-800',
    'ministere_commerce': 'bg-purple-100 text-purple-800',
    'impots': 'bg-red-100 text-red-800',
    'reparations': 'bg-orange-100 text-orange-800',
    'salaires': 'bg-green-100 text-green-800',
    'honoraires': 'bg-indigo-100 text-indigo-800',
    'factures': 'bg-yellow-100 text-yellow-800',
    'loyer': 'bg-pink-100 text-pink-800'
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setPageLoading(true);
        
        const today = new Date().toISOString().split('T')[0];
        const entriesRes = await axios.get(`${API}/expenses?date=${today}`).catch(() => ({ data: [] }));
        
        setRecentEntries(Array.isArray(entriesRes.data) ? entriesRes.data.slice(0, 10) : []);
        
      } catch (error) {
        console.error('Error loading expense data:', error);
        setRecentEntries([]);
      } finally {
        setPageLoading(false);
      }
    };
    
    loadData();
  }, [API]);

  const handleJustificationUpload = (event) => {
    try {
      const files = Array.from(event.target.files || []);
      
      files.forEach(file => {
        if (file.type.startsWith('image/') || file.type.includes('pdf') || file.type.includes('document')) {
          const reader = new FileReader();
          reader.onload = (e) => {
            try {
              const base64 = e.target.result;
              setJustifications(prev => [...prev, {
                id: Date.now() + Math.random(),
                name: file.name,
                type: file.type,
                size: file.size,
                data: base64
              }]);
            } catch (error) {
              console.error('Error processing justification:', error);
            }
          };
          reader.readAsDataURL(file);
        } else {
          toast.error(`Type de fichier non supporté: ${file.name}`);
        }
      });
      
      event.target.value = '';
    } catch (error) {
      console.error('Error uploading justification:', error);
      toast.error('Erreur lors du téléchargement');
    }
  };

  const removeJustification = (justificationId) => {
    try {
      setJustifications(prev => prev.filter(doc => doc.id !== justificationId));
    } catch (error) {
      console.error('Error removing justification:', error);
    }
  };

  const downloadJustification = (justification) => {
    try {
      const link = document.createElement('a');
      link.href = justification.data;
      link.download = justification.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading justification:', error);
      toast.error('Erreur lors du téléchargement');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileType) => {
    if (fileType.includes('pdf')) return <File className="h-4 w-4 text-red-600" />;
    if (fileType.startsWith('image/')) return <Image className="h-4 w-4 text-blue-600" />;
    return <FileText className="h-4 w-4 text-gray-600" />;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (!selectedType || !amount || !description.trim()) {
        toast.error('Veuillez remplir tous les champs obligatoires');
        return;
      }

      if (parseFloat(amount) <= 0) {
        toast.error('Le montant doit être positif');
        return;
      }

      setLoading(true);

      const justificationData = justifications.map(doc => ({
        name: doc.name,
        type: doc.type,
        size: doc.size,
        data: doc.data
      }));
      
      await axios.post(`${API}/expenses`, {
        expense_type: selectedType,
        amount: parseInt(amount),
        description: description.trim(),
        justifications: justificationData,
        user_id: user.id
      });

      toast.success('Dépense enregistrée avec succès !');
      
      // Reset form
      setSelectedType('');
      setAmount('');
      setDescription('');
      setJustifications([]);
      
      // Refresh recent entries
      const today = new Date().toISOString().split('T')[0];
      const entriesRes = await axios.get(`${API}/expenses?date=${today}`);
      if (Array.isArray(entriesRes.data)) {
        setRecentEntries(entriesRes.data.slice(0, 10));
      }
    } catch (error) {
      console.error('Error creating expense record:', error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (entryId) => {
    try {
      if (!entryId) return;
      
      if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette dépense ?')) {
        return;
      }

      await axios.delete(`${API}/expenses/${entryId}?user_role=${user.role}`);
      toast.success('Dépense supprimée !');
      
      const today = new Date().toISOString().split('T')[0];
      const entriesRes = await axios.get(`${API}/expenses?date=${today}`);
      if (Array.isArray(entriesRes.data)) {
        setRecentEntries(entriesRes.data.slice(0, 10));
      }
    } catch (error) {
      console.error('Error deleting expense record:', error);
      if (error.response?.status === 403) {
        toast.error('Accès refusé');
      } else {
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  const canDelete = () => {
    return user && (user.role === 'owner' || user.role === 'system_admin');
  };

  const getExpenseTypeDetails = (typeId) => {
    return expenseTypes.find(t => t.id === typeId);
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'Heure inconnue';
    try {
      return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Heure invalide';
    }
  };

  const formatCurrency = (amount) => {
    return `${amount.toLocaleString()} F CFA`;
  };

  if (pageLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="spinner mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement des dépenses...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
      <Navigation />
      
      <div className="content-wrapper px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-width-container flex items-center justify-between mb-8 fade-in">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Saisie des Dépenses
            </h1>
            <p className="text-gray-600">
              Enregistrez les dépenses quotidiennes pour le suivi comptable
            </p>
          </div>
        </div>

        <div className="max-width-cards grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Expense Form */}
          <Card className="bg-white/70 glass border-0 shadow-lg slide-up">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wallet className="h-5 w-5 text-red-600" />
                Nouvelle Dépense
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Type de Dépense - HTML SELECT NATIF */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    Type de dépense *
                  </Label>
                  <select
                    value={selectedType}
                    onChange={(e) => {
                      console.log('Expense type selected:', e.target.value);
                      setSelectedType(e.target.value);
                    }}
                    className="w-full h-12 px-4 text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white hover:border-red-400 transition-colors"
                    data-testid="expense-type-select-safe"
                    required
                  >
                    <option value="" disabled>Sélectionnez un type de dépense</option>
                    {expenseTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Montant */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    Montant (F CFA) *
                  </Label>
                  <Input
                    type="number"
                    min="1"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="h-12 text-base"
                    placeholder="Ex: 15000"
                    data-testid="amount-input"
                    required
                  />
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    Description *
                  </Label>
                  <Textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="min-h-[100px] text-base"
                    placeholder="Décrivez la dépense (motif, fournisseur, etc.)"
                    data-testid="description-input"
                    required
                  />
                </div>

                {/* Justificatifs Upload */}
                <div className="space-y-2">
                  <Label htmlFor="justifications" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Upload className="h-4 w-4" />
                    Justificatifs (factures, reçus, etc.)
                  </Label>
                  <input
                    id="justifications"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    multiple
                    onChange={handleJustificationUpload}
                    className="hidden"
                  />
                  
                  <Button
                    type="button"
                    onClick={() => document.getElementById('justifications').click()}
                    variant="outline"
                    className="w-full h-12 border-2 border-dashed border-gray-300 hover:border-blue-400 bg-gray-50 hover:bg-blue-50"
                    data-testid="justification-upload-btn"
                  >
                    <Upload className="h-5 w-5 mr-2" />
                    Ajouter des justificatifs (PDF, Images)
                  </Button>
                  
                  {/* Justifications Preview */}
                  {justifications.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-700 flex items-center gap-2">
                        <File className="h-4 w-4" />
                        {justifications.length} document(s) sélectionné(s)
                      </p>
                      <div className="space-y-2 max-h-32 overflow-y-auto">
                        {justifications.map((doc) => (
                          <div key={doc.id} className="flex items-center justify-between p-2 bg-gray-100 rounded-lg">
                            <div className="flex items-center gap-2">
                              {getFileIcon(doc.type)}
                              <div>
                                <p className="text-sm font-medium text-gray-900 truncate max-w-40">
                                  {doc.name}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {formatFileSize(doc.size)}
                                </p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeJustification(doc.id)}
                              className="p-1 hover:bg-red-100 rounded transition-colors"
                              title="Supprimer ce document"
                            >
                              <X className="h-4 w-4 text-red-500" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Amount Preview */}
                {amount && (
                  <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="h-5 w-5 text-red-600" />
                        <span className="text-sm font-medium text-red-800">
                          Montant de la dépense
                        </span>
                      </div>
                      <span className="text-lg font-bold text-red-600" data-testid="amount-preview">
                        {formatCurrency(parseInt(amount))}
                      </span>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={loading || !selectedType || !amount || !description.trim()}
                  className="w-full h-12 bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white font-medium rounded-lg shadow-lg"
                  data-testid="submit-expense-btn"
                >
                  {loading ? (
                    <>
                      <div className="spinner mr-2"></div>
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Enregistrer la Dépense
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Recent Expenses */}
          <Card className="bg-white/70 glass border-0 shadow-lg slide-up" style={{animationDelay: '0.1s'}}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-red-600" />
                Dépenses Récentes
              </CardTitle>
            </CardHeader>
            <CardContent>
              {Array.isArray(recentEntries) && recentEntries.length > 0 ? (
                <div className="space-y-3">
                  {recentEntries.map((entry, index) => {
                    if (!entry || !entry.id) return null;
                    
                    const typeDetails = getExpenseTypeDetails(entry.expense_type);
                    const Icon = expenseIcons[entry.expense_type];
                    
                    return (
                      <div key={entry.id} className="p-3 bg-gray-50 rounded-lg border-l-4 border-red-500">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-3">
                            {Icon && (
                              <div className={`p-2 rounded-full ${expenseColors[entry.expense_type] || 'bg-gray-100 text-gray-600'}`}>
                                <Icon className="h-4 w-4" />
                              </div>
                            )}
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">
                                {typeDetails?.name || 'Type inconnu'}
                              </p>
                              <p className="text-sm text-gray-600">
                                {formatTime(entry.time)} • {entry.description}
                              </p>
                              
                              {/* Justificatifs Display */}
                              {entry.justifications && entry.justifications.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1">
                                  {entry.justifications.slice(0, 3).map((doc, docIndex) => (
                                    <button
                                      key={docIndex}
                                      onClick={() => downloadJustification(doc)}
                                      className="flex items-center gap-1 px-2 py-1 bg-blue-50 hover:bg-blue-100 rounded text-xs text-blue-700 transition-colors"
                                      title={`Télécharger ${doc.name}`}
                                    >
                                      {getFileIcon(doc.type)}
                                      <span className="max-w-16 truncate">{doc.name}</span>
                                    </button>
                                  ))}
                                  {entry.justifications.length > 3 && (
                                    <span className="text-xs text-gray-500 px-2 py-1">
                                      +{entry.justifications.length - 3} autres
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <Badge variant="destructive" className="mb-1">
                              -{formatCurrency(entry.amount)}
                            </Badge>
                            {entry.justifications && entry.justifications.length > 0 && (
                              <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                                <File className="h-3 w-3" />
                                {entry.justifications.length} document(s)
                              </p>
                            )}
                            {canDelete() && (
                              <button
                                onClick={() => handleDelete(entry.id)}
                                className="p-1 hover:bg-red-100 rounded transition-colors group mt-1 block"
                                title="Supprimer cette dépense"
                                data-testid={`delete-expense-${entry.id}`}
                              >
                                <Trash2 className="h-3 w-3 text-gray-400 group-hover:text-red-600" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  }).filter(Boolean)}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Wallet className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Aucune dépense enregistrée aujourd'hui</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Expense Types */}
        <Card className="max-width-cards mt-8 bg-white/70 glass border-0 shadow-lg">
          <CardHeader>
            <CardTitle>Types de Dépenses Fréquents</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {expenseTypes.map((type) => {
                const Icon = expenseIcons[type.id];
                return (
                  <button
                    key={type.id}
                    onClick={() => {
                      setSelectedType(type.id);
                      document.getElementById('amount')?.focus();
                    }}
                    className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                      selectedType === type.id
                        ? 'border-red-500 bg-red-50'
                        : 'border-gray-300 hover:border-red-400'
                    }`}
                  >
                    {Icon && <Icon className="h-8 w-8 mx-auto mb-2 text-gray-600" />}
                    <p className="text-sm font-medium text-center">{type.name}</p>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
      
      <Footer />
    </div>
  );
};

export default ExpenseEntryFixed;